// Sidebar.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Transition } from "@headlessui/react";
import { Tooltip } from "@mui/material";
import clsx from "clsx";
import * as PiIcons from "react-icons/pi";
import {
  PiGearLight,
  PiQuestionLight,
  PiSignOutLight,
  PiUserLight,
  PiBuildingOfficeLight,
  PiCaretDownLight,
  PiCaretRightLight,
  PiPlusLight,
  PiListLight,
  PiPaletteLight,
  PiTableLight,
  PiLockSimpleLight,
  PiBookOpenTextLight,
} from "react-icons/pi";

import { RootState, useAppDispatch } from "../../store";
import { toggleSidebar } from "../../store/actions/SidebarActions";
import { isTourForceLocal, setTourForceLocal } from "../../utils/tourGuidance";
import { getAiModels, getUserType } from "../../store/actions/ChatAction";
import { fetchUserSubscriptionStatus } from "../../store/actions/SubscriptionActions";
import { setChatList } from "../../store/actions/DataAction";
import { toggleSidepanel, setUnreadCount, addNotification } from "../Notifications/notificationSlice";
import { updateNotification } from "../../store/actions/Notifications";
import { ThemeContext } from "../../contexts/ThemeContext";
import Logo from "../shared/brand/Logo";
import { hasAccessToFeature } from "../../utils/permissionUtils";
import { isBetaEnv } from "../../utils/authUtils";
import ProfileImage from "../Header/ProfileImage";
import OnboardingWidget from "../Management/OnboardingWidget";
import Skeleton from "../shared/TailwindComponents/Skeleton";
import { useAuth0 } from "@auth0/auth0-react";
import { setTokenProvider } from "../../utils/apis";
import apis from "../../utils/apis";
import { MenuItem, isAdminOnlyItem } from "../../services/MenuService";
import { fetchMenuStructure } from "../../store/actions/MenuActions";
import { clearToken } from "../../store/actions/SignInAction";
import { useLicenseGate } from "../../hooks/useLicenseGate";
import { getFilteredMenu } from "../../config/defaultMenu";

// [OK] PubSub Notifications service
import { getNotificationsService } from "../../services/PubSubNotificationsService";
import { NotificationToastContainer, ToastNotification } from "../Notifications/NotificationToast";
import Badge from "../shared/TailwindComponents/Badge";

// Which nav row carries the selected mark - see `navActive.ts` for the rule.
import { isNavItemActive } from "./navActive";
import { toErrorText } from '../../utils/errorMessage';

// Hardcoded fallback menu structure (temporary until DB is synced)
const FALLBACK_MENU_STRUCTURE: MenuItem[] = [
  // Flat navigation: primary entities
  { id: 1,  label: "Home",          path: "/home",     icon_key: "PiHouseLight",              sort_order: 0, is_parent: false, allowed_roles: "all" },
  { id: 7,  label: "Projects",      path: "/projects", icon_key: "PiProjectorScreenChartLight", sort_order: 1, is_parent: false, add_button: true, allowed_roles: "all" },
  { id: 8,  label: "Matters",       path: "/matters",  icon_key: "PiBriefcaseLight",          sort_order: 2, is_parent: false, add_button: true, allowed_roles: "all" },
  { id: 42, label: "Contracts",     path: "/contracts", icon_key: "PiScrollLight",            sort_order: 2.5, is_parent: false, add_button: false, allowed_roles: "all" },
  { id: 5,  label: "Contacts",      path: "/contacts", icon_key: "PiAddressBookLight",        sort_order: 3, is_parent: false, add_button: true, allowed_roles: "all", divider_after: true },
  // Flat navigation: daily workflow
  { id: 2,  label: "Tasks",         path: "/tasks",    icon_key: "PiCheckSquareOffsetLight",  sort_order: 4, is_parent: false, add_button: true, allowed_roles: "all" },
  { id: 3,  label: "Calendar",      path: "/calendar", icon_key: "PiCalendarLight",           sort_order: 5, is_parent: false, allowed_roles: "all" },
  { id: 4,  label: "Messages",      path: "/messages", icon_key: "PiChatsCircleLight",        sort_order: 6, is_parent: false, allowed_roles: "all" },
  { id: 24, label: "Notifications", path: "#",         icon_key: "PiBellLight",               sort_order: 7, is_parent: false, allowed_roles: "all" },
  { id: 9,  label: "Files",         path: "/files",    icon_key: "PiFileLight",               sort_order: 8, is_parent: false, allowed_roles: "all", divider_after: true },
  // Section: Tools
  {
    id: 22, label: "Tools", path: null, sort_order: 9, is_parent: true, allowed_roles: "all",
    children: [
      { id: 10, label: "Research",  path: "/research",  icon_key: "PiListMagnifyingGlassLight", sort_order: 1, add_button: false, allowed_roles: "all" },
      { id: 31, label: "Reviews",   path: "/reviews",   icon_key: "PiFileMagnifyingGlassLight", sort_order: 2, add_button: true,  allowed_roles: "all" },
      { id: 11, label: "Tables",    path: "/tables",    icon_key: "PiTableLight",               sort_order: 3, add_button: true,  allowed_roles: "all" },
    ],
  },
  // Section: Knowledge
  {
    id: 35, label: "Knowledge", path: null, sort_order: 10, is_parent: true, allowed_roles: "all",
    children: [
      { id: 15, label: "Templates",    path: "/templates",    icon_key: "PiFileTextLight", sort_order: 1, add_button: true,  allowed_roles: "all" },
      { id: 13, label: "Data Sources", path: "/data-sources", icon_key: "PiDatabaseLight", sort_order: 2, add_button: true,  allowed_roles: "all" },
      { id: 12, label: "Analytics",    path: "/analytics",    icon_key: "PiChartBarLight", sort_order: 3, add_button: false, allowed_roles: "all" },
    ],
  },
  // Section: Organization
  {
    id: 23, label: "Organization", path: null, sort_order: 11, is_parent: true, allowed_roles: "all",
    children: [
      { id: 16, label: "People",      path: "/people",      icon_key: "PiUsersLight",      sort_order: 1, add_button: false, allowed_roles: "all" },
      { id: 17, label: "Departments", path: "/departments", icon_key: "PiBuildingLight",   sort_order: 2, add_button: true,  allowed_roles: "all" },
      { id: 18, label: "Teams",       path: "/teams",       icon_key: "PiUsersThreeLight", sort_order: 3, add_button: true,  allowed_roles: "all" },
      { id: 19, label: "Finance",     path: "/finance",     icon_key: "PiCoinsLight",      sort_order: 4, add_button: false, allowed_roles: "all" },
    ],
  },
  // Bottom bar
  {
    id: 25, label: "Bottom", path: null, sort_order: 12, is_parent: true, allowed_roles: "all",
    children: [
      { id: 26, label: "Settings", path: "/settings", icon_key: "PiGearLight",     sort_order: 1, add_button: false, allowed_roles: "all" },
      { id: 27, label: "Help",     path: "/help",     icon_key: "PiQuestionLight", sort_order: 2, add_button: false, allowed_roles: "all" },
    ],
  },
  // User account menu
  {
    id: 28, label: "User", path: null, sort_order: 13, is_parent: true, allowed_roles: "all",
    children: [
      { id: 29, label: "Account",         path: "/account", icon_key: "PiUserLight", sort_order: 1, add_button: false, allowed_roles: "all" },
      // Admin Dashboard retired from the nav - see the note in `config/defaultMenu.ts`.
      // { id: 30, label: "Admin Dashboard", path: "/admin",   icon_key: "PiGearLight", sort_order: 2, add_button: false, allowed_roles: "admin_only" },
    ],
  },
];

const Sidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslationSafe();
  const [forceTours, setForceTours] = useState(isTourForceLocal());

  const userType = getUserType();
  const isOpen = useSelector((state: RootState) => state.sidebar.isOpen);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const unreadNotifications = useSelector((state: RootState) => state.notifications.unreadCount);
  const isNotificationPanelOpen = useSelector((state: RootState) => state.notifications.isOpen);

  const preferences = useSelector((state: RootState) => state.lists.preferences);
  const whiteLabelSettings = useSelector((state: RootState) => state.whiteLabel.settings);
  const isLoading = useSelector((state: RootState) => state.whiteLabel.loading);

  // Get menu from Redux (persisted across navigation)
  const menuStructure = useSelector((state: RootState) => state.menu?.menuStructure);
  const menuLoading = useSelector((state: RootState) => state.menu?.loading);
  const menuError = useSelector((state: RootState) => state.menu?.error);

  const { theme } = useContext(ThemeContext);
  const isDarkMode = theme === "dark";

  const chatList = useSelector((state: RootState) => state.lists.chatList);
  const currentUID = userInfo?.UserID;

  const userMenus = useSelector((state: RootState) => state.auth.menus);

  const { logout, getAccessTokenSilently, isAuthenticated } = useAuth0();

  // License gate - check before allowing "+" create actions and dim locked sections
  const { checkRouteGate, hasSectionAccess, sectionForRoute } = useLicenseGate();

  // [OK] FIX: do not crash on first render
  const rawUserId = userInfo?.UserID;
  const userId = String(rawUserId ?? "");

  const isWhiteLabelEnabled =
    preferences?.WhiteLableEnabled === true || preferences?.WhiteLableEnabled === "1";

  const [unreadChatCounts, setUnreadChatCounts] = useState<Record<string, number>>(() => {
    const stored = localStorage.getItem("unreadChatCounts");
    return stored ? JSON.parse(stored) : {};
  });

  const [openSections, setOpenSections] = useState({
    tools: true,
    knowledge: true,
    organization: true,
  });

  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [isTemplatesMenuOpen, setIsTemplatesMenuOpen] = useState(false);
  const [templatesMenuPosition, setTemplatesMenuPosition] = useState({ top: 0, left: 0 });
  const templatesMenuRef = useRef<HTMLDivElement>(null);
  const templatesButtonRef = useRef<HTMLButtonElement>(null);

  // [OK] NEW: ensure token provider is set before initializing PubSub
  const [tokenReady, setTokenReady] = useState(false);

  // [OK] PubSub notifications service + init guard to prevent duplicate initialize/join
  const notifServiceRef = useRef<ReturnType<typeof getNotificationsService> | null>(null);
  const notifInitRef = useRef<{ userId?: string; initialized: boolean }>({
    userId: undefined,
    initialized: false,
  });

  // [OK] Session error listener for interceptor-detected session issues
  const sessionErrorListenerRef = useRef<((event: CustomEvent) => void) | null>(null);

  // Toast notification queue for real-time PubSub notifications
  const [toasts, setToasts] = useState<ToastNotification[]>([]);
  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ----------------------------------------------------------------------------
  // Helpers
  // ----------------------------------------------------------------------------
  const isValidUserId = (u: any) => {
    const s = String(u ?? "").trim();
    return !!s && s !== "null" && s !== "undefined";
  };

  const handleToggleSidebar = () => dispatch(toggleSidebar());

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleAccountMenu = () => setIsAccountMenuOpen((v) => !v);

  // Close templates menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideMenu = templatesMenuRef.current?.contains(target);
      const isClickOnButton = templatesButtonRef.current?.contains(target);
      if (!isClickInsideMenu && !isClickOnButton) {
        setIsTemplatesMenuOpen(false);
      }
    };
    if (isTemplatesMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isTemplatesMenuOpen]);

  const handleLogout = useCallback(async () => {
    try {
      await apis({
        method: 'POST',
        url: '/devices/logout-all',
      });
    } catch (error) {
      // Logout endpoint error - fail silently
    }

    // Clear all stored credentials and data
    localStorage.clear();
    sessionStorage.clear();

    // Dispatch Redux action to clear token state
    dispatch(clearToken());

    // Perform Auth0 logout with redirect
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [dispatch, logout]);

  const getIconByKey = (iconKey: string): JSX.Element => {
    const IconComponent = PiIcons[iconKey as keyof typeof PiIcons] as any;
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <></>;
  };

  const disabledNavItems = isBetaEnv ? ["dashboard", "marketplace", "finance"] : [];

  /*
    THE nav corner radius, for the row AND the "+" on it.

    They were `rounded-sm` (12px, from the theme's `--radius`) and `rounded`
    (4px, Tailwind's own default) - two scales, so the little button and the
    row it sits in never agreed at the corner. One constant now, used by both,
    which is the only way they stay matched when either is touched again.

    That constant is `rounded-lg` - the theme's `--radius` (1rem) itself, the
    same token every card in the app corners on (`GlassCard` is
    `glass rounded-lg`). A nav row is ~28px tall, so the browser clamps 16px
    to half the box and the row reads as a soft pill; a card is tall enough to
    show the full 16px. Same token either way, which is the point: the rail
    and the cards beside it stop disagreeing about what "rounded" means.
  */
  const NAV_RADIUS = 'rounded-lg';

  /**
   * The "+" on a nav row.
   *
   * On a SELECTED row the glyph inherits `text-bg` - paper on the dark active
   * pill in light mode, ink on the light one in dark mode - and the old hover
   * painted `bg-gray-200` behind it either way. In light mode that is a pale
   * chip under a near-white glyph: the plus effectively disappeared at the
   * moment you reached for it.
   *
   * So the hover on a selected row DARKENS the pill it is on instead. `ink`
   * is the brand's dark primitive and does not flip with the theme, so one
   * value reads as "a shade deeper than this pill" against both the dark
   * active row in light mode and the light one in dark mode.
   */
  const navAddButtonClass = (isActive: boolean) => clsx(
    'p-1 transition-colors duration-200',
    NAV_RADIUS,
    isActive
      ? 'hover:bg-ink/25'
      : 'text-gray-500 dark:text-gray-400 hover:bg-surface-sunken hover:text-fg-strong',
  );

  const getNavItemClass = (path: string, isActive: boolean) => {
    const isDisabled = disabledNavItems.includes(path.replace("/", ""));
    return clsx(
      // The radius is `NAV_RADIUS`, shared with the "+" button on the row —
      // see the constant. Hover and selected backgrounds inherit it, so the
      // fill always follows the same corner as the button inside it.
      "flex items-center ps-2 pe-2 py-1 text-[11px] transition-all duration-300 relative",
      NAV_RADIUS,
      isActive && !isDisabled && "bg-fg-strong text-bg [&_svg]:text-bg",
      !isActive &&
        !isDisabled &&
        "hover:bg-surface-soft",
      isDisabled && "text-gray-400 dark:text-gray-600 cursor-not-allowed",
      !isOpen && "justify-center"
    );
  };

  const renderNavLink = (
    to: string,
    label: string,
    icon: JSX.Element,
    badge?: number,
    onClick?: () => void,
    addButton: boolean = false,
    key?: string
  ) => {
    // Safety check - don't render items without valid path or label
    if (!to || !label) return null;

    // Feature flag badges for preview/beta features.
    // Cleared for the EU release - no "Early" badges in the sidebar.
    const betaRoutes: Record<string, string> = {};
    const betaLabel = betaRoutes[to] || null;
    
    const isDisabled = disabledNavItems.includes(to.replace("/", ""));

    // License check - is this section locked?
    const routeSection = sectionForRoute(to);
    const isLicenseLocked = routeSection ? !hasSectionAccess(routeSection) : false;

    // Special handling for templates - render dropdown menu for template type
    const isTemplatesRoute = to === '/templates';

    const handleTemplatesButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (templatesButtonRef.current) {
        const rect = templatesButtonRef.current.getBoundingClientRect();
        setTemplatesMenuPosition({
          top: rect.top,
          left: rect.right + 8
        });
      }
      setIsTemplatesMenuOpen(!isTemplatesMenuOpen);
    };

    const templatesDropdown = isTemplatesMenuOpen && ReactDOM.createPortal(
      <div 
        ref={templatesMenuRef}
        className="w-56 rounded-md bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5"
        style={{ 
          position: 'fixed',
          zIndex: 99999, 
          top: templatesMenuPosition.top,
          left: templatesMenuPosition.left,
        }}
      >
        <div className="py-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              history.push('/templates/add?type=structure');
              setIsTemplatesMenuOpen(false);
            }}
            className="flex items-center w-full text-start px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <PiListLight className="h-4 w-4 me-2" />
            {t('Structure Template')}
          </button>
          {/* <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              history.push('/templates/add?type=branding');
              setIsTemplatesMenuOpen(false);
            }}
            className="flex items-center w-full text-start px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <PiPaletteLight className="h-4 w-4 me-2" />
            {t('Branding Template')}
          </button> */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              history.push('/templates/add?type=table-columns');
              setIsTemplatesMenuOpen(false);
            }}
            className="flex items-center w-full text-start px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <PiTableLight className="h-4 w-4 me-2" />
            {t('Table Columns Template')}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              history.push('/templates/add?type=playbooks');
              setIsTemplatesMenuOpen(false);
            }}
            className="flex items-center w-full text-start px-4 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <PiBookOpenTextLight className="h-4 w-4 me-2" />
            {t('Playbook')}
          </button>
        </div>
      </div>,
      document.body
    );
    
    const content = isTemplatesRoute ? (
      <>
        <NavLink to={to} className={getNavItemClass(to, isNavItemActive(location.pathname, to))} onClick={onClick}>
          <div className="flex items-center w-full">
            {icon}
            {isOpen && <span className="ms-2">{t(label)}</span>}
            {isOpen && betaLabel && <Badge color="blue" className="ms-1.5 !text-[9px] !px-1 !py-0">{betaLabel}</Badge>}
            <div className="ms-auto flex items-center space-x-1">
              {addButton && isOpen && (
                <button
                  ref={templatesButtonRef}
                  onClick={handleTemplatesButtonClick}
                  className={navAddButtonClass(isNavItemActive(location.pathname, to))}
                >
                  <PiPlusLight className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </NavLink>
        {templatesDropdown}
      </>
    ) : (
      <NavLink to={to} className={getNavItemClass(to, isNavItemActive(location.pathname, to))} onClick={onClick}>
        <div className="flex items-center w-full">
          {icon}
          {isOpen && <span className="ms-2">{t(label)}</span>}
          {isOpen && betaLabel && <Badge color="blue" className="ms-1.5 !text-[9px] !px-1 !py-0">{betaLabel}</Badge>}
          <div className="ms-auto flex items-center space-x-1">
            {addButton && isOpen && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (checkRouteGate(to)) {
                    history.push(`${to}?add=true`);
                  }
                }}
                className={navAddButtonClass(isNavItemActive(location.pathname, to))}
              >
                <PiPlusLight className="h-3 w-3" />
              </button>
            )}
            {(badge as any) > 0 && <span className="text-black pe-1">{badge}</span>}
          </div>
        </div>
      </NavLink>
    );

    return (
      <li key={key}>
        {isDisabled ? (
          <Tooltip title={t("Coming Soon")} placement="right">
            <div className={getNavItemClass(to, false)}>
              {icon}
              {isOpen && <span className="ms-2">{t(label)}</span>}
              {isOpen && betaLabel && <Badge color="blue" className="ms-1.5 !text-[9px] !px-1 !py-0">{betaLabel}</Badge>}
            </div>
          </Tooltip>
        ) : isLicenseLocked ? (
          <Tooltip title={isOpen ? '' : `${t(label)} - ${t('Upgrade to unlock')}`} placement="right">
            <NavLink
              to={to}
              className={clsx(getNavItemClass(to, isNavItemActive(location.pathname, to)), 'opacity-50')}
            >
              <div className="flex items-center w-full">
                {icon}
                {isOpen && <span className="ms-2">{t(label)}</span>}
                <div className="ms-auto flex items-center">
                  <PiLockSimpleLight className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
            </NavLink>
          </Tooltip>
        ) : isOpen ? (
          content
        ) : (
          <Tooltip title={badge ? `${t(label)} (${badge})` : t(label)} placement="right">
            <NavLink to={to} className={getNavItemClass(to, isNavItemActive(location.pathname, to))} onClick={onClick}>
              <div className="relative">
                {icon}
                {(badge as any) > 0 && (
                  <span className="absolute -top-1.5 -end-1.5 flex items-center justify-center min-w-[16px] h-[16px] px-[3px] text-[9px] font-semibold leading-none text-white bg-info-500 rounded-full shadow-sm ring-2 ring-gray-50 dark:ring-gray-900 animate-[notif-badge-pop_0.3s_ease-out]">
                    {(badge as number) > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
            </NavLink>
          </Tooltip>
        )}
      </li>
    );
  };

  const renderSection = (
    title: string,
    sectionKey: keyof typeof openSections,
    items: {
      to: string;
      label: string;
      icon: JSX.Element;
      badge?: number;
      condition?: boolean;
      onClick?: () => void;
      addButton?: boolean;
    }[],
    key?: string
  ) => {
    const filteredItems = items.filter((item) => item.condition !== false);
    if (!filteredItems.length) return null;

    return (
      <div key={key} className="space-y-2">
        {isOpen && (
          <button
            onClick={() => toggleSection(sectionKey)}
            className="flex items-center justify-between w-full ps-2 pe-3 py-1 text-[10px] font-light text-gray-500 dark:text-gray-500 tracking-widest transition-colors duration-300"
          >
            {t(title)}
            <PiCaretDownLight className={clsx("h-3 w-3", openSections[sectionKey] && "rotate-180")} />
          </button>
        )}
        <ul className={clsx("space-y-2", isOpen && openSections[sectionKey] && "ms-2")}>
          {filteredItems.map((item, index) =>
            isOpen && !openSections[sectionKey]
              ? null
              : renderNavLink(item.to, item.label, item.icon, item.badge, item.onClick, item.addButton ?? false, `${item.to}-${index}`)
          )}
        </ul>
        <div className="border-b border-gray-200 dark:border-gray-700 my-2" />
      </div>
    );
  };

  // ----------------------------------------------------------------------------
  // Unread Chat Counts (existing logic)
  // ----------------------------------------------------------------------------
  useEffect(() => {
    const unreadCountsFromChatList: Record<string, number> = {};

    chatList.forEach((chat: any) => {
      if (!currentUID || !chat.ChatID || !chat.LastMessage) return;

      const isUserOriginalSender = chat.SenderID === currentUID;
      const isUserOriginalReceiver = chat.RecieverID === currentUID;

      const lastMsgFromOther = chat.LastMessage?.SenderID !== currentUID;
      if (!lastMsgFromOther) return;

      let count = 0;
      if (isUserOriginalSender && chat.UnreadCountSender > 0) count = chat.UnreadCountSender;
      else if (isUserOriginalReceiver && chat.UnreadCountReciever > 0) count = chat.UnreadCountReciever;

      if (count > 0) unreadCountsFromChatList[chat.ChatID] = count;
    });

    setUnreadChatCounts(unreadCountsFromChatList);
  }, [chatList, currentUID]);

  useEffect(() => {
    localStorage.setItem("unreadChatCounts", JSON.stringify(unreadChatCounts));
  }, [unreadChatCounts]);

  // Existing socket events for chat unread counts
  useEffect(() => {
    const handleNewChatMessage = (data: { chat_id: string }) => {
      const chatId = data.chat_id;
      if (!location.pathname.includes("/messages")) {
        setUnreadChatCounts((prev) => {
          const updated = { ...prev, [chatId]: (prev[chatId] || 0) + 1 };
          localStorage.setItem("unreadChatCounts", JSON.stringify(updated));
          return updated;
        });
      }
    };

    const handleChatSeen = (data: { chat_id: string }) => {
      const chatId = data.chat_id;

      if (location.pathname.includes("/messages")) {
        setUnreadChatCounts((prev) => {
          const updated = { ...prev };
          delete updated[chatId];
          localStorage.setItem("unreadChatCounts", JSON.stringify(updated));
          return updated;
        });

        const updatedChatList = chatList.map((chat: any) => {
          if (chat.ChatID === chatId) {
            const isSender = chat.SenderID === currentUID;
            const isReceiver = chat.RecieverID === currentUID;

            return {
              ...chat,
              UnreadCountSender: isSender ? 0 : chat.UnreadCountSender,
              UnreadCountReciever: isReceiver ? 0 : chat.UnreadCountReciever,
            };
          }
          return chat;
        });

        dispatch(setChatList(updatedChatList));
      }
    };

    // Socket listeners removed - legacy code
  }, [location.pathname, chatList, dispatch, currentUID]);

  // ----------------------------------------------------------------------------
  // Menu fetch (from Redux - persisted across navigation)
  // Only fetches on authentication, not on every navigation
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (isAuthenticated && !menuStructure) {
      dispatch(fetchMenuStructure());
    }
  }, [isAuthenticated, dispatch]);

  // Re-build menu when org_type becomes available (loaded async from /auth/profile)
  useEffect(() => {
    if (isAuthenticated && userInfo?.org_type) {
      dispatch(fetchMenuStructure(true));
    }
  }, [isAuthenticated, userInfo?.org_type, dispatch]);

  // ----------------------------------------------------------------------------
  // Auth token provider setup
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthenticated) return;

    setTokenProvider(async () => {
      try {
        return await getAccessTokenSilently();
      } catch (e: any) {
        const m = toErrorText(e?.error) || toErrorText(e?.message) || "";
        if (
          m.includes("missing_refresh_token") ||
          m.includes("login_required") ||
          m.includes("consent_required")
        ) {
          return null;
        }
        throw e;
      }
    });

    setTokenReady(true);
  }, [isAuthenticated, getAccessTokenSilently]);

  // ----------------------------------------------------------------------------
  // Session error listener (from API interceptor)
  // When the interceptor detects SESSION_MISSING or SESSION_INVALID,
  // it dispatches a 'session-error' event. This listener triggers the
  // complete logout flow to match manual logout behavior.
  // ----------------------------------------------------------------------------
  useEffect(() => {
    const handleSessionError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const errorCode = customEvent.detail?.code;

      // Trigger logout for any session-related error
      if (
        errorCode === 'SESSION_MISSING' ||
        errorCode === 'SESSION_INVALID' ||
        errorCode === 'SESSION_VALIDATION_FAILED' ||
        errorCode === 'UNAUTHORIZED'
      ) {
        handleLogout();
      }
    };

    sessionErrorListenerRef.current = handleSessionError as any;
    window.addEventListener('session-error', handleSessionError as unknown as EventListener);

    return () => {
      if (sessionErrorListenerRef.current) {
        window.removeEventListener('session-error', sessionErrorListenerRef.current as unknown as EventListener);
      }
    };
  }, [handleLogout]);

  // ----------------------------------------------------------------------------
  // [OK] Notifications via PubSub
  // ----------------------------------------------------------------------------

  // Use refs so the PubSub callbacks always call the latest handler
  // without forcing the init useEffect to re-run (and accidentally
  // unregister + never re-register listeners).
  const onNewNotificationRef = useRef<(data: any) => void>(() => {});
  const onUnreadCountRef = useRef<(data: any) => void>(() => {});
  const onLicenseRevokedRef = useRef<(data: any) => void>(() => {});
  const onManualQueryRef = useRef<(data: any) => void>(() => {});

  // Keep refs pointing at the latest logic on every render
  onNewNotificationRef.current = (data: any) => {
    const notification = data?.notification;
    if (!notification) return;

    dispatch(addNotification(notification));

    // Suppress toasts when the notification panel is already open
    if (isNotificationPanelOpen) return;

    // Show a sidebar-anchored toast for this notification
    setToasts((prev) => [
      ...prev,
      {
        id: String(notification.notification_id || `toast_${Date.now()}`),
        title: notification.title,
        content: notification.content,
        link: notification.link,
        type: notification.notification_type || notification.type,
      },
    ]);
  };

  onUnreadCountRef.current = (data: { unread_count: number }) => {
    dispatch(setUnreadCount(Number(data?.unread_count || 0)));
  };

  onLicenseRevokedRef.current = (_data: any) => {
    dispatch(getAiModels());
    dispatch(fetchUserSubscriptionStatus());
  };

  onManualQueryRef.current = (_data: any) => {
    dispatch(getAiModels());
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!tokenReady) return;
    if (!isValidUserId(userId)) return;

    const uid = String(userId);

    // Prevent duplicate initialize - only run once per userId
    if (notifInitRef.current.initialized && notifInitRef.current.userId === uid) return;
    notifInitRef.current = { userId: uid, initialized: true };

    const svc = getNotificationsService(uid);
    notifServiceRef.current = svc;

    let offNew: (() => void) | null = null;
    let offUnread: (() => void) | null = null;
    let offSync: (() => void) | null = null;
    let offRevoked: (() => void) | null = null;
    let offManual: (() => void) | null = null;

    let cancelled = false;

    (async () => {
      try {
        const ok = await svc.initialize(uid);
        if (!ok || cancelled) {
          notifInitRef.current = { userId: uid, initialized: false };
          return;
        }

        // Register stable wrappers that delegate to refs (never go stale)
        offNew = svc.onNotificationNew((d) => onNewNotificationRef.current(d));
        offUnread = svc.onUnreadCountUpdated((d) => onUnreadCountRef.current(d));

        offSync = svc.onSyncResult((res: any) => {
          dispatch(setUnreadCount(Number(res?.unread_count || 0)));
        });

        offRevoked = svc.onLicenseRevoked((d) => onLicenseRevokedRef.current(d));
        offManual = svc.onManualQueryUpdate((d) => onManualQueryRef.current(d));
      } catch (e) {
        notifInitRef.current = { userId: uid, initialized: false };
      }
    })();

    return () => {
      cancelled = true;
      try { offNew?.(); } catch {}
      try { offUnread?.(); } catch {}
      try { offSync?.(); } catch {}
      try { offRevoked?.(); } catch {}
      try { offManual?.(); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, tokenReady, userId]);

  // ----------------------------------------------------------------------------
  // Render dynamic menu
  // ----------------------------------------------------------------------------
  // Menu filtered by environment (EU hides unreleased paths) + org type
  const effectiveMenuStructure = getFilteredMenu(userInfo?.org_type);

  const renderDynamicMenu = () => {
    if (menuLoading && (!menuStructure || menuStructure.length === 0)) {
      return isOpen ? <Skeleton variant="list" rows={8} inset="sm" /> : null;
    }

    // Render standalone items (flat nav: Home, Projects, Matters, Contacts, Tasks, Calendar, etc.)
    const standaloneItems = effectiveMenuStructure
      .filter((item: MenuItem) => !item.is_parent && item.path && item.label)
      .map((item: MenuItem) => {
        // Badge support for Messages and Notifications in flat nav
        let badge: number | undefined;
        let onClick: (() => void) | undefined;

        if (item.label === 'Notifications') {
          badge = unreadNotifications;
          onClick = () => dispatch(toggleSidepanel());
        } else if (item.label === 'Messages') {
          badge = Object.keys(unreadChatCounts).length;
        }

        return (
          <React.Fragment key={`standalone-${item.id}`}>
            <ul className={clsx('space-y-2', isOpen && 'ms-2')}>
              {renderNavLink(
                item.path!,
                item.label,
                item.icon_key ? getIconByKey(item.icon_key) : <></>,
                badge,
                onClick,
                item.add_button || false,
                `nav-standalone-${item.id}`
              )}
            </ul>
            {item.divider_after && (
              <div className="border-b border-gray-200 dark:border-gray-700 my-2" />
            )}
          </React.Fragment>
        );
      });

    const sections = effectiveMenuStructure
      .filter((parentItem: MenuItem) => {
        // Filter out special parents that are rendered elsewhere
        if (!parentItem.is_parent || !parentItem.children) return false;

        // "Bottom" and "User" are handled separately, not in main menu
        if (parentItem.label === "Bottom" || parentItem.label === "User") {
          return false;
        }

        return true;
      })
      .map((parentItem: MenuItem, index: number) => {
        const sectionKey = parentItem.label.toLowerCase().replace(/\s+/g, "") as keyof typeof openSections;
        const title = parentItem.label;

        const items = (parentItem.children || [])
          .map((child: MenuItem) => {
            if (!child.path || !child.label) return null;

            const baseItem = {
              to: child.path,
              label: child.label,
              icon: child.icon_key ? getIconByKey(child.icon_key) : <></>,
              addButton: child.add_button || false,
            };

            if (child.label === "Notifications") {
              return { ...baseItem, badge: unreadNotifications, onClick: () => dispatch(toggleSidepanel()) };
            }

            if (child.label === "Messages") {
              return { ...baseItem, badge: Object.keys(unreadChatCounts).length };
            }

            return baseItem;
          })
          .filter(Boolean) as any[];

        return renderSection(title, sectionKey, items, `section-${sectionKey}-${index}`);
      })
      .filter(Boolean);

    return [...standaloneItems, ...sections];
  };

  return (
    <div
      className={clsx(
        "flex flex-col bg-bg text-fg-body transition-all duration-300 ease-slide border-e border-hairline h-screen relative",
        isOpen ? "w-52" : "w-14"
      )}
    >
      {/*
        COLLAPSED-RAIL AXIS, part 3 of 3 - the logo.

        `p-2` here is the same 8px gutter the nav and the bottom block use, so
        the header's usable column is the same 39px theirs is and the mark's
        centre is the rail's centre. That was already true by arithmetic and
        only by arithmetic: the link was a shrink-to-fit flex item that the
        parent's `justify-center` happened to land in the middle. Anything
        else added to this header - a badge, a white-label `<img>` beside the
        mark - and `justify-between`'s sibling would drag it off again.

        Collapsed, the link now OWNS the column (`w-full justify-center`), so
        the mark sits on the axis because it is centred in the same box as the
        icons, not because nothing else is in the row. Expanded, the header is
        untouched: `justify-between` still spaces the wordmark against the
        edge exactly as before.
      */}
      <div className={clsx("flex items-center p-2 h-12 border-b border-gray-200 dark:border-gray-700", isOpen ? "justify-between" : "justify-center")}>
        <NavLink to="/matters" className={clsx("flex items-center", !isOpen && "w-full justify-center")}>
          {isLoading ? (
            <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 animate-fadePulse rounded" />
          ) : isOpen ? (
            <Logo variant="wordmark" className="h-8 w-auto ms-2" />
          ) : (
            <Logo variant="mark" className="w-6 h-6" />
          )}
        </NavLink>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {/*
          COLLAPSED-RAIL AXIS, part 1 of 3 - the icon column.

          `custom-scrollbar` is a 2px webkit scrollbar. 2px is nothing on a
          content pane, but this nav is 39px of usable width, and the scrollbar
          takes its 2px out of the CONTENT box: every `justify-center` icon
          below it lands 1px left of the rail's true centre, while the logo in
          the header (no scroller, full 39px) sits ON it. So the rail had two
          axes 1px apart, and the logo read as the one that was off.

          Collapsed, the rail hides its scrollbar instead - a 2px thumb in a
          56px icon rail is not a usable target anyway - so the nav centres on
          the same 39px column the header does. Expanded, nothing changes.
        */}
        <nav className={clsx("h-full px-2 py-3 overflow-y-auto space-y-2", isOpen ? "custom-scrollbar" : "scrollbar-none")}>{renderDynamicMenu()}</nav>
        {isOpen && (
          <button
            onClick={handleToggleSidebar}
            className={clsx("absolute bottom-2 end-2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition", NAV_RADIUS)}
          >
            <PiCaretRightLight
              className="h-3 w-3 transition-transform duration-300 text-gray-500 rotate-180 rtl:-scale-x-100"
            />
          </button>
        )}
      </div>

      {!isOpen && (
        <div className="px-2 py-1 flex justify-center">
          <button
            onClick={handleToggleSidebar}
            className={clsx("p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition", NAV_RADIUS)}
          >
            <PiCaretRightLight
              className="h-3 w-3 transition-transform duration-300 text-gray-500 rtl:-scale-x-100"
            />
          </button>
        </div>
      )}

      <div className="p-2 border-t border-gray-300 dark:border-gray-700">
        <ul className="space-y-2">
          {effectiveMenuStructure
            .find((parent) => parent.label === "Bottom" && parent.is_parent)
            ?.children?.filter((item) => item.path && item.label)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map((item, index) =>
              renderNavLink(item.path!, item.label, getIconByKey(item.icon_key || ""), undefined, undefined, item.add_button || false, `bottom-${item.path}-${index}`)
            )
          }
        </ul>

        {/* Product guidance controls - the user-facing "Guided tips" toggle now
            lives in Settings → General; only the dev override remains here. */}
        <div className="mt-2 space-y-1">
          {process.env.NODE_ENV === 'development' && (
            <button
              type="button"
              onClick={() => {
                const next = !forceTours;
                setTourForceLocal(next);
                setForceTours(next);
                window.location.reload();
              }}
              className={clsx(
                "flex items-center gap-2 w-full ps-2 pe-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                NAV_RADIUS,
                !isOpen && "justify-center",
              )}
              title="Dev only - re-show every tour regardless of completion, for local testing"
            >
              <PiIcons.PiInfoLight className="h-5 w-5 flex-shrink-0 text-warning-500" />
              {isOpen && (
                <>
                  <span className="text-sm flex-1 text-start">Force tips (dev)</span>
                  <span className={clsx(
                    'text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                    forceTours
                      ? 'text-warning-600 bg-warning-50 dark:bg-warning-900/30 dark:text-warning-400'
                      : 'text-gray-500 bg-gray-100 dark:bg-gray-700 dark:text-gray-400',
                  )}>
                    {forceTours ? 'On' : 'Off'}
                  </span>
                </>
              )}
            </button>
          )}
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between">
            <button
              onClick={toggleAccountMenu}
              className={clsx(
                // COLLAPSED-RAIL AXIS, part 2 of 3 - the avatar.
                //
                // This row never got the `justify-center` every other collapsed
                // row has, so the 28px avatar sat flush against its 8px padding
                // and its centre landed ~2.5px RIGHT of the icons above it —
                // the bottom of the rail disagreeing with the top of it.
                //
                // `rounded-e` also cornered only the right-hand side, which is
                // the one row in the rail whose hover fill had square left
                // corners. It takes `NAV_RADIUS` like every other row now.
                "flex items-center ps-2 pe-2 py-1 w-full hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent dark:hover:from-gray-700 dark:hover:to-transparent transition-all duration-300",
                NAV_RADIUS,
                !isOpen && "justify-center",
              )}
            >
              <ProfileImage
                profilePicture={userInfo?.profile_picture || userInfo?.ProfilePicture || null}
                initials={`${userInfo?.FirstName?.[0] || "U"}${userInfo?.LastName?.[0] || ""}`}
                email={userInfo?.email || userInfo?.Email || ""}
              />
              {isOpen && (
                <div className="ms-2 text-start">
                  <p className="text-[10px]">{`${userInfo?.FirstName || ""} ${
                    userInfo?.LastName || ""
                  }`}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-24">{userInfo?.Email}</p>
                </div>
              )}
            </button>

            {isOpen && <OnboardingWidget />}
          </div>

          <Transition
            show={isAccountMenuOpen}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <div className="absolute bottom-12 start-2 w-52 glass rounded-lg shadow-lg p-2 z-50">
              {effectiveMenuStructure
                .find((parent) => parent.label === "User" && parent.is_parent)
                ?.children?.map((item) => {
                  // Skip admin items if user is not org admin
                  if (isAdminOnlyItem(item) && !userInfo?.is_org_admin) {
                    return null;
                  }

                  return item.path ? (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className="flex items-center px-2 py-1 text-[10px] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-300"
                      onClick={() => setIsAccountMenuOpen(false)}
                    >
                      {item.icon_key && getIconByKey(item.icon_key)}
                      {!item.icon_key && <PiUserLight className="h-5 w-5" />}
                      <span className="ms-2">{t(item.label)}</span>
                    </NavLink>
                  ) : null;
                })
              }
              {/* Admin Dashboard retired from the nav - organization administration
                  now lives in Settings ▸ Administration. This fallback link used to
                  re-add it whenever the menu structure lacked `/admin`, so it must
                  stay commented out alongside the menu entry in
                  `config/defaultMenu.ts`. Re-enable both together. */}
              {/* {userInfo?.is_org_admin && !effectiveMenuStructure.find((p) => p.label === "User")?.children?.some((c) => c.path === "/admin") && (
                <NavLink
                  to="/admin"
                  className="flex items-center px-2 py-1 text-[10px] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-300"
                  onClick={() => setIsAccountMenuOpen(false)}
                >
                  <PiGearLight className="h-5 w-5 me-2" />
                  {t("Admin Dashboard")}
                </NavLink>
              )} */}

              <button
                onClick={handleLogout}
                className="flex items-center px-2 py-1 text-[10px] hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md w-full text-start transition-colors duration-300"
              >
                <PiSignOutLight className="h-5 w-5 me-2" />
                {t("Sign out")}
              </button>
            </div>
          </Transition>
        </div>
      </div>

      {/* Real-time notification toasts - anchored to sidebar edge */}
      <NotificationToastContainer
        toasts={toasts}
        onDismiss={dismissToast}
        onMarkAsRead={(notificationId: string) => dispatch(updateNotification(notificationId))}
        sidebarWidth={isOpen ? 208 : 56}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------
// Small helper: keep this file compiling even if your i18n hook changes later.
// ----------------------------------------------------------------------------
function useTranslationSafe() {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const mod = require("react-i18next");
  const hook = mod?.useTranslation;
  if (typeof hook === "function") return hook();
  return { t: (s: string) => s };
}

// ----------------------------------------------------------------------------
// Scrollbar CSS injection (as in your original file)
// ----------------------------------------------------------------------------
const customScrollbarCSS = `
  .custom-scrollbar::-webkit-scrollbar { width: 2px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
    transition: background 0.3s;
  }
  .custom-scrollbar:hover::-webkit-scrollbar-thumb { background: rgba(5, 9, 21, 0.5); }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(17, 32, 76, 0.8); }

  @keyframes notif-badge-pop {
    0%   { transform: scale(0); opacity: 0; }
    60%  { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  }
`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = customScrollbarCSS;
document.head.appendChild(styleSheet);

export default Sidebar;
